/**
 * Analytics tracker — first-party, all data stored in Blink DB only.
 *
 * Architecture:
 *  - analytics_visitors   — one row per unique browser/device (persistent)
 *  - analytics_sessions   — one row per session (30-min inactivity = new session)
 *  - analytics_page_views — one row per page view / route change
 *  - analytics_events     — clicks, form_submits, CTA interactions
 *  - analytics_replay_events — privacy-safe session replay events
 *
 * Privacy rules:
 *  - visitor_id: stored in localStorage (persistent per browser)
 *  - session_id: stored in sessionStorage (new after 30min inactivity)
 *  - NO sensitive form data captured in replay
 *  - All analytics errors are silent — never break the public site
 */

const PROXY_URL = 'https://98va6f1d--admin-db-proxy.functions.blink.new';

// ─── Storage keys ─────────────────────────────────────────────────────────────

const VISITOR_KEY        = '_sa_vid';
const VISITOR_SEEN_KEY   = '_sa_vseen';   // first seen timestamp
const SESSION_KEY        = '_sa_sid';
const SESSION_TS_KEY     = '_sa_sts';     // last activity timestamp
const SESSION_STARTED_KEY = '_sa_sst';   // session started_at
const SESSION_ENTRY_KEY  = '_sa_sep';    // session entry path
const PAGE_COUNT_KEY     = '_sa_spc';    // page count in session
const SESSION_START_TS   = '_sa_sstart'; // session start ms for duration calc

const SESSION_TIMEOUT_MS  = 30 * 60 * 1000; // 30 min
const REPLAY_MIN_DURATION = 30 * 1000;      // 30 sec before saving replay (was 5 min)
const REPLAY_PAUSE_MS     = 5 * 60 * 1000;  // 5 min inactivity pauses replay (was 2 min)
const REPLAY_MIN_EVENTS   = 3;              // or at least 3 events (was 15)

// ─── Visitor ID ───────────────────────────────────────────────────────────────

export function getVisitorId(): string {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem(VISITOR_KEY, vid);
      localStorage.setItem(VISITOR_SEEN_KEY, new Date().toISOString());
    }
    return vid;
  } catch {
    return `v_${Date.now().toString(36)}`;
  }
}

function getVisitorFirstSeen(): string {
  try {
    return localStorage.getItem(VISITOR_SEEN_KEY) || new Date().toISOString();
  } catch {
    return new Date().toISOString();
  }
}

// ─── Session management ───────────────────────────────────────────────────────

interface SessionInfo {
  sessionId: string;
  isNew: boolean;
  startedAt: string;
  entryPath: string;
}

function getOrCreateSession(path: string): SessionInfo {
  try {
    const now = Date.now();
    const lastTs = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || '0', 10);
    const elapsed = now - lastTs;

    let sid = sessionStorage.getItem(SESSION_KEY);
    let isNew = false;

    if (!sid || elapsed > SESSION_TIMEOUT_MS) {
      sid = `s_${now.toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
      const startedAt = new Date().toISOString();
      sessionStorage.setItem(SESSION_KEY, sid);
      sessionStorage.setItem(SESSION_STARTED_KEY, startedAt);
      sessionStorage.setItem(SESSION_ENTRY_KEY, path);
      sessionStorage.setItem(PAGE_COUNT_KEY, '0');
      sessionStorage.setItem(SESSION_START_TS, String(now));
      isNew = true;
    }

    sessionStorage.setItem(SESSION_TS_KEY, String(now));

    return {
      sessionId: sid,
      isNew,
      startedAt: sessionStorage.getItem(SESSION_STARTED_KEY) || new Date().toISOString(),
      entryPath: sessionStorage.getItem(SESSION_ENTRY_KEY) || path,
    };
  } catch {
    const sid = `s_${Date.now().toString(36)}`;
    return { sessionId: sid, isNew: true, startedAt: new Date().toISOString(), entryPath: path };
  }
}

function incrementPageCount(): number {
  try {
    const current = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '0', 10);
    const next = current + 1;
    sessionStorage.setItem(PAGE_COUNT_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}

function getSessionDurationMs(): number {
  try {
    const start = parseInt(sessionStorage.getItem(SESSION_START_TS) || '0', 10);
    return start > 0 ? Date.now() - start : 0;
  } catch {
    return 0;
  }
}

// ─── Device / Browser detection ───────────────────────────────────────────────

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobi|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (/edg\//i.test(ua)) return 'Edge';
  if (/opr\//i.test(ua)) return 'Opera';
  if (/chrome|crios/i.test(ua)) return 'Chrome';
  if (/firefox|fxios/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua)) return 'Safari';
  if (/msie|trident/i.test(ua)) return 'IE';
  return 'Other';
}

function getOS(): string {
  const ua = navigator.userAgent;
  if (/windows/i.test(ua)) return 'Windows';
  if (/android/i.test(ua)) return 'Android';
  if (/ipad|iphone|ipod/i.test(ua)) return 'iOS';
  if (/mac/i.test(ua)) return 'macOS';
  if (/linux/i.test(ua)) return 'Linux';
  return 'Other';
}

// ─── Traffic source detection ─────────────────────────────────────────────────

function detectSource(referrer: string): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    if (utmSource) return utmSource.toLowerCase();
    const utmMedium = params.get('utm_medium');
    if (utmMedium === 'cpc' || utmMedium === 'paid') return 'ads';
  } catch { /* ignore */ }

  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (host === window.location.hostname) return 'direct';
    if (host.includes('google')) return 'google';
    if (host.includes('yandex')) return 'yandex';
    if (host.includes('instagram') || host === 'l.instagram.com') return 'instagram';
    if (host.includes('facebook') || host.includes('fb.com') || host.includes('fb.me')) return 'facebook';
    if (host.includes('t.me') || host.includes('telegram')) return 'telegram';
    if (host.includes('youtube') || host.includes('youtu.be')) return 'youtube';
    if (host.includes('twitter') || host.includes('t.co') || host.includes('x.com')) return 'twitter';
    return 'referral';
  } catch {
    return 'direct';
  }
}

// ─── Core send (non-blocking, keepalive fetch) ────────────────────────────────

function uid(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function safeSend(body: Record<string, unknown>): void {
  try {
    fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* silent */ });
  } catch { /* never break the app */ }
}

// ─── Visitor upsert ───────────────────────────────────────────────────────────

function upsertVisitor(visitorId: string): void {
  try {
    const firstSeen = getVisitorFirstSeen();
    const payload = {
      id: visitorId,
      visitorId,
      firstSeenAt: firstSeen,
      lastSeenAt: new Date().toISOString(),
      deviceType: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      language: navigator.language || '',
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      metaJson: '{}',
    };
    safeSend({ action: 'upsert', table: 'analyticsVisitors', payload });
  } catch { /* silent */ }
}

// ─── Session upsert ───────────────────────────────────────────────────────────

function upsertSession(
  sessionId: string,
  visitorId: string,
  entryPath: string,
  startedAt: string,
  source: string,
  referrerUrl: string,
  pageCount: number,
): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const payload = {
      id: sessionId,
      sessionId,
      visitorId,
      startedAt,
      lastSeenAt: new Date().toISOString(),
      entryPath,
      referrer: referrerUrl,
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
      source,
      deviceType: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      language: navigator.language || '',
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      pageCount,
      metaJson: '{}',
    };
    safeSend({ action: 'upsert', table: 'analyticsSessions', payload });
  } catch { /* silent */ }
}

// ─── Page view insert ─────────────────────────────────────────────────────────

function insertPageView(
  visitorId: string,
  sessionId: string,
  path: string,
  source: string,
  referrerUrl: string,
): void {
  try {
    const payload = {
      id: `pv_${uid()}`,
      visitorId,
      sessionId,
      path,
      pageTitle: document.title || path,
      referrer: referrerUrl,
      source,
      viewedAt: new Date().toISOString(),
      metaJson: JSON.stringify({
        device: getDeviceType(),
        browser: getBrowser(),
      }),
    };
    safeSend({ action: 'upsert', table: 'analyticsPageViews', payload });
  } catch { /* silent */ }
}

// ─── Event insert ─────────────────────────────────────────────────────────────

function insertEvent(
  visitorId: string,
  sessionId: string,
  eventType: string,
  eventName: string,
  path: string,
  meta: Record<string, string> = {},
): void {
  try {
    const referrerUrl = document.referrer || '';
    const source = detectSource(referrerUrl);
    const params = new URLSearchParams(window.location.search);

    const payload = {
      id: `ev_${uid()}`,
      visitorId,
      sessionId,
      eventType,
      eventName,
      path,
      referrer: source,
      createdAt: new Date().toISOString(),
      metaJson: JSON.stringify({
        source,
        referrerUrl,
        utmSource: params.get('utm_source') || '',
        utmMedium: params.get('utm_medium') || '',
        utmCampaign: params.get('utm_campaign') || '',
        device: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
        language: navigator.language || '',
        label: eventName,
        ...meta,
      }),
    };
    safeSend({ action: 'upsert', table: 'analyticsEvents', payload });
  } catch { /* silent */ }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Track page view. Call on every route change.
 * Creates/updates visitor, session, and writes page_view record.
 */
export function trackPageView(path?: string): void {
  try {
    const p = path ?? window.location.pathname;
    const referrerUrl = document.referrer || '';
    const source = detectSource(referrerUrl);
    const visitorId = getVisitorId();
    const session = getOrCreateSession(p);
    const pageCount = incrementPageCount();

    // Upsert visitor record
    upsertVisitor(visitorId);

    // Upsert session (creates or updates lastSeenAt + pageCount)
    upsertSession(
      session.sessionId,
      visitorId,
      session.entryPath,
      session.startedAt,
      source,
      referrerUrl,
      pageCount,
    );

    // Write page view to dedicated table
    insertPageView(visitorId, session.sessionId, p, source, referrerUrl);
  } catch { /* silent */ }
}

/**
 * Track a button click or CTA interaction.
 * @param label - human-readable button label
 * @param path - current path (auto-detected if omitted)
 * @param meta - additional metadata
 */
export function trackClick(label: string, path?: string, meta?: Record<string, string>): void {
  try {
    const p = path ?? window.location.pathname;
    const visitorId = getVisitorId();
    const session = getOrCreateSession(p);
    insertEvent(visitorId, session.sessionId, 'click', label, p, meta ?? {});
  } catch { /* silent */ }
}

/**
 * Track form submission.
 */
export function trackFormSubmit(formName: string, path?: string): void {
  try {
    const p = path ?? window.location.pathname;
    const visitorId = getVisitorId();
    const session = getOrCreateSession(p);
    insertEvent(visitorId, session.sessionId, 'form_submit', formName, p);
  } catch { /* silent */ }
}

// ─── Session Replay ───────────────────────────────────────────────────────────

/**
 * Privacy-safe session replay.
 * Records: clicks, scrolls, pointer moves (sampled), route changes.
 * NEVER records: form field values, passwords, emails, phone numbers.
 */

interface ReplayBuffer {
  events: ReplayEvent[];
  sessionId: string;
  visitorId: string;
  lastActivity: number;
  paused: boolean;
  flushing: boolean;
}

interface ReplayEvent {
  type: string;
  path: string;
  x: number;
  y: number;
  scrollY: number;
  viewportW: number;
  viewportH: number;
  elementTag: string;
  elementId: string;
  elementClass: string;
  maskedValue: string;
  ts: string;
}

let _replayBuffer: ReplayBuffer | null = null;
let _replayFlushTimer: ReturnType<typeof setTimeout> | null = null;

// Sensitive element selectors — never record their values
const SENSITIVE_SELECTORS = [
  'input[type="password"]',
  'input[type="email"]',
  'input[type="tel"]',
  'input[name*="phone"]',
  'input[name*="email"]',
  'input[name*="password"]',
  'textarea[name*="message"]',
  'textarea',
];

function isSensitiveElement(el: Element | null): boolean {
  if (!el) return false;
  return SENSITIVE_SELECTORS.some(sel => {
    try { return el.matches(sel); } catch { return false; }
  });
}

function getSafeElementInfo(el: Element | null): { tag: string; id: string; cls: string } {
  if (!el) return { tag: '', id: '', cls: '' };
  return {
    tag: el.tagName?.toLowerCase() || '',
    id: isSensitiveElement(el) ? '[masked]' : (el.id || '').slice(0, 40),
    cls: isSensitiveElement(el) ? '[masked]' : ((el as HTMLElement).className || '').toString().slice(0, 80),
  };
}

function shouldSaveReplay(buf: ReplayBuffer): boolean {
  const duration = Date.now() - parseInt(sessionStorage.getItem(SESSION_START_TS) || '0', 10);
  return duration >= REPLAY_MIN_DURATION || buf.events.length >= REPLAY_MIN_EVENTS;
}

function flushReplayBuffer(buf: ReplayBuffer, force = false): void {
  if (buf.flushing) return;
  if (!force && !shouldSaveReplay(buf)) return;
  if (buf.events.length === 0) return;

  buf.flushing = true;
  const events = buf.events.splice(0, 50); // flush up to 50 events at a time

  for (const ev of events) {
    try {
      safeSend({
        action: 'upsert',
        table: 'analyticsReplayEvents',
        payload: {
          id: `rp_${uid()}`,
          visitorId: buf.visitorId,
          sessionId: buf.sessionId,
          eventType: ev.type,
          path: ev.path,
          x: ev.x,
          y: ev.y,
          scrollY: ev.scrollY,
          viewportW: ev.viewportW,
          viewportH: ev.viewportH,
          elementTag: ev.elementTag,
          elementId: ev.elementId,
          elementClass: ev.elementClass,
          maskedValue: ev.maskedValue,
          createdAt: ev.ts,
          metaJson: '{}',
        },
      });
    } catch { /* silent */ }
  }

  buf.flushing = false;
}

function pushReplayEvent(type: string, data: Partial<ReplayEvent>): void {
  if (!_replayBuffer) return;
  const buf = _replayBuffer;

  // Check inactivity pause
  const now = Date.now();
  if (now - buf.lastActivity > REPLAY_PAUSE_MS) {
    buf.paused = true;
  }
  buf.lastActivity = now;
  buf.paused = false;

  const ev: ReplayEvent = {
    type,
    path: window.location.pathname,
    x: data.x ?? 0,
    y: data.y ?? 0,
    scrollY: data.scrollY ?? window.scrollY,
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    elementTag: data.elementTag ?? '',
    elementId: data.elementId ?? '',
    elementClass: data.elementClass ?? '',
    maskedValue: '',
    ts: new Date().toISOString(),
  };

  buf.events.push(ev);

  // Auto-flush every 30 events
  if (buf.events.length >= 30) {
    flushReplayBuffer(buf);
  }
}

let _pointerSampleCount = 0;

function initReplay(visitorId: string, sessionId: string): void {
  if (_replayBuffer?.sessionId === sessionId) return; // already init for this session

  _replayBuffer = {
    events: [],
    sessionId,
    visitorId,
    lastActivity: Date.now(),
    paused: false,
    flushing: false,
  };

  // Click tracking
  document.addEventListener('click', (e) => {
    const target = e.target as Element;
    const info = getSafeElementInfo(target);
    pushReplayEvent('click', {
      x: Math.round(e.clientX),
      y: Math.round(e.clientY),
      elementTag: info.tag,
      elementId: info.id,
      elementClass: info.cls,
    });
  }, { passive: true });

  // Scroll tracking — sampled at 1Hz (was 2Hz). Reduces per-session scroll
  // events by ~50% without losing scroll-depth analytics granularity.
  let _lastScroll = 0;
  document.addEventListener('scroll', () => {
    const now = Date.now();
    if (now - _lastScroll < 1000) return; // sample every 1s (was 500ms)
    _lastScroll = now;
    pushReplayEvent('scroll', { scrollY: window.scrollY });
  }, { passive: true });

  // Pointer move — very sparse sampling (1 per ~3 seconds at 60Hz). Was 1/40,
  // now 1/80 — halves pointer-move replay traffic.
  document.addEventListener('mousemove', (e) => {
    _pointerSampleCount++;
    if (_pointerSampleCount % 80 !== 0) return; // very sparse sampling
    pushReplayEvent('pointer', {
      x: Math.round(e.clientX),
      y: Math.round(e.clientY),
    });
  }, { passive: true });

  // Touch (mobile)
  document.addEventListener('touchstart', (e) => {
    const t = e.touches[0];
    if (!t) return;
    pushReplayEvent('touch', {
      x: Math.round(t.clientX),
      y: Math.round(t.clientY),
    });
  }, { passive: true });

  // Periodic flush every 30s. Combined with sparser scroll/pointer sampling
  // and the existing 50-events-per-flush cap, this halves the typical
  // background replay traffic without losing replay fidelity.
  _replayFlushTimer = setInterval(() => {
    if (_replayBuffer) flushReplayBuffer(_replayBuffer);
  }, 30_000);

  // Flush on page unload — `pagehide` fires reliably on mobile/iOS where
  // `beforeunload` is not guaranteed (especially on tab switching). We also
  // listen to `visibilitychange` so the replay buffer is flushed when the
  // user backgrounds the tab. This is the modern, recommended replacement
  // for `beforeunload` for analytics-style write-on-exit logic.
  const flushOnExit = () => {
    if (_replayBuffer) flushReplayBuffer(_replayBuffer, true);
  };
  window.addEventListener('pagehide', flushOnExit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnExit();
  });

  // Route change flush
  window.addEventListener('popstate', () => {
    if (_replayBuffer) {
      pushReplayEvent('route_change', {});
      flushReplayBuffer(_replayBuffer);
    }
  });
}

// ─── Retention cleanup (call periodically) ───────────────────────────────────

/**
 * Clean up analytics data older than 7 days.
 * Called from the admin edge function or can be triggered manually.
 * On frontend we just make a best-effort call to a cleanup endpoint.
 */
export function triggerRetentionCleanup(): void {
  // Retention cleanup is handled server-side by admin-db-proxy
  // Just log intent — actual deletion would need a dedicated cleanup function
  // This is a no-op on the frontend for now
}

// ─── Full page/session bootstrap ─────────────────────────────────────────────

/**
 * Full bootstrap: call once when app loads and on every route change.
 * Handles visitor, session, page_view, and initializes replay.
 */
export function bootstrapAnalytics(path?: string): void {
  try {
    const p = path ?? window.location.pathname;
    const referrerUrl = document.referrer || '';
    const source = detectSource(referrerUrl);
    const visitorId = getVisitorId();
    const session = getOrCreateSession(p);
    const pageCount = incrementPageCount();

    // Upsert visitor
    upsertVisitor(visitorId);

    // Upsert session
    upsertSession(
      session.sessionId,
      visitorId,
      session.entryPath,
      session.startedAt,
      source,
      referrerUrl,
      pageCount,
    );

    // Write page view
    insertPageView(visitorId, session.sessionId, p, source, referrerUrl);

    // Initialize session replay
    initReplay(visitorId, session.sessionId);

    // Heartbeat: update lastSeenAt every 30 seconds so online counter stays accurate
    startSessionHeartbeat(visitorId, session.sessionId, session.entryPath, session.startedAt, source, referrerUrl);
  } catch { /* silent */ }
}

// ─── Session Heartbeat ────────────────────────────────────────────────────────

let _heartbeatTimer: ReturnType<typeof setInterval> | null = null;

function startSessionHeartbeat(
  visitorId: string,
  sessionId: string,
  entryPath: string,
  startedAt: string,
  source: string,
  referrerUrl: string,
): void {
  // Clear any previous heartbeat
  if (_heartbeatTimer) clearInterval(_heartbeatTimer);

  _heartbeatTimer = setInterval(() => {
    try {
      // Only send heartbeat if session is still active (same sessionStorage session)
      const currentSid = sessionStorage.getItem(SESSION_KEY);
      if (currentSid !== sessionId) { clearInterval(_heartbeatTimer!); return; }

      // Update lastSeenAt on the session via proxy
      const pageCount = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || '1', 10);
      upsertSession(sessionId, visitorId, entryPath, startedAt, source, referrerUrl, pageCount);
      sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()));
    } catch { /* silent */ }
  }, 30_000); // every 30 seconds
}
